/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file change background in call controls button
 * @author Liam Liden
 * @module Epic.VideoApp.Components.Header.Buttons.ChangeBackgroundButton
 */

import { useDispatch } from "@epic/react-redux-booster";
import React, { FC, useCallback } from "react";
import { useStrings } from "~/hooks";
import blurryBackground from "~/icons/blurryBackground";
import { uiActions, useBackgroundProcessorsState, useUIState } from "~/state";
import { determineMenuActionType } from "~/state/ui";
import ControlButton from "./ControlButton";

// button's ID in HTML used to focus it from BaseMenu
export const changeBackgroundButtonId = "background-button";

enum TokenNames {
	changeBackground = "ChangeBackground",
	backgrounds = "Backgrounds",
}

/**
 * The ChangeBackgroundButton component
 * @param props The props ;)
 */
const ChangeBackgroundButton: FC = () => {
	const visibleMenu = useUIState((selectors) => selectors.getVisibleMenu(), []);
	const isProcessorsLoaded =
		useBackgroundProcessorsState((selectors) => selectors.getProcessorLoadStatus(), []) === "finished";

	const dispatch = useDispatch();

	const strings = useStrings("ChangeBackgroundButton", Object.values(TokenNames));

	const onClick = useCallback(
		(event?: React.MouseEvent<HTMLButtonElement>) => {
			const actionType = determineMenuActionType(event);
			dispatch(uiActions.toggleVisibleMenu({ menu: "background", actionType }));
		},
		[dispatch],
	);

	if (!isProcessorsLoaded) {
		return null;
	}

	const pressed = visibleMenu === "background";

	return (
		<ControlButton
			id={changeBackgroundButtonId}
			icon={blurryBackground}
			ariaLabel={strings[TokenNames.changeBackground]}
			onClick={onClick}
			pressed={pressed}
			keyboardShortcut={["alt", "b"]}
			supplementaryIcon="continue"
			buttonText={strings[TokenNames.backgrounds]}
		/>
	);
};

ChangeBackgroundButton.displayName = "ChangeBackgroundButton";

export default ChangeBackgroundButton;
